import { create } from 'zustand';

/**
 * @fileoverview Tooltip store (Zustand)
 * @description
 * React/Zustand port of the Angular `TooltipService`. Manages tooltip
 * show/hide/position state for truncated-text tooltips. Method names
 * (`showTooltip`, `hideTooltip`, `hideTooltipImmediate`, `updatePosition`),
 * the default config, the show/hide delays, the truncation detection, and the
 * auto-positioning math are reproduced 1:1.
 *
 * Adaptation notes:
 * - Angular imperatively created a `TooltipComponent` via `createComponent` and
 *   appended it to a body container. In React the tooltip is rendered by a host
 *   component that subscribes to this store; the store exposes reactive state
 *   (`visible`, `content`, `maxWidth`, `coords`, `position`) instead of doing
 *   DOM component instantiation. The host applies `coords`/`position` to the
 *   rendered `.tooltip-content` element.
 * - The scroll/resize listeners (debounced position updates) and the global
 *   mouse-leave listener are wired via an idempotent `init()` and `destroy()`.
 */

export interface TooltipPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface TooltipConfig {
  content: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  delay: number;
  maxWidth: number;
  offset: number;
}

/** Computed placement applied to the rendered tooltip element. */
export interface TooltipPlacement {
  top: number;
  left: number;
  position: 'top' | 'bottom' | 'left' | 'right' | 'auto';
}

interface TooltipState {
  /** Whether the tooltip is currently shown */
  visible: boolean;
  /** Tooltip text content */
  content: string;
  /** Max width in px */
  maxWidth: number;
  /** Computed placement coords for the rendered element */
  placement: TooltipPlacement | null;

  showTooltip: (element: HTMLElement, config: Partial<TooltipConfig>) => void;
  hideTooltip: () => void;
  hideTooltipImmediate: () => void;
  updatePosition: () => void;
  /** Wire scroll/resize listeners. Idempotent. */
  init: () => void;
  /** Cleanup listeners + timers. */
  destroy: () => void;
}

const DEFAULT_CONFIG: TooltipConfig = {
  content: '',
  position: 'auto',
  delay: 500,
  maxWidth: 300,
  offset: 8,
};

let hideTimeout: ReturnType<typeof setTimeout> | null = null;
let showTimeout: ReturnType<typeof setTimeout> | null = null;
let currentTarget: HTMLElement | null = null;
let currentConfig: TooltipConfig = { ...DEFAULT_CONFIG };
let scrollHandler: (() => void) | null = null;
let resizeHandler: (() => void) | null = null;
let scrollDebounce: ReturnType<typeof setTimeout> | null = null;
let resizeDebounce: ReturnType<typeof setTimeout> | null = null;
let initialized = false;

/** Checks if text is truncated in the element (was private isTextTruncated). */
function isTextTruncated(element: HTMLElement): boolean {
  const isHorizontalTruncated = element.scrollWidth > element.clientWidth;
  const isVerticalTruncated = element.scrollHeight > element.clientHeight;
  return isHorizontalTruncated || isVerticalTruncated;
}

/** Calculates element position relative to viewport (was private calculatePosition). */
function calculatePosition(element: HTMLElement): TooltipPosition {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

/**
 * Compute final placement coords for the tooltip element (was positionTooltip).
 * Reads the live `.tooltip-content` element to measure tooltip dimensions.
 */
function computePlacement(
  targetPosition: TooltipPosition,
  config: TooltipConfig,
): TooltipPlacement | null {
  const tooltip = document.querySelector('.tooltip-content') as HTMLElement | null;
  if (!tooltip) return null;

  const tooltipRect = tooltip.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let position = config.position;
  let top = 0;
  let left = 0;

  if (position === 'auto') {
    const spaceTop = targetPosition.top;
    const spaceBottom = viewportHeight - (targetPosition.top + targetPosition.height);
    const spaceLeft = targetPosition.left;
    const spaceRight = viewportWidth - (targetPosition.left + targetPosition.width);

    if (spaceTop >= tooltipRect.height + config.offset) {
      position = 'top';
    } else if (spaceBottom >= tooltipRect.height + config.offset) {
      position = 'bottom';
    } else if (spaceRight >= tooltipRect.width + config.offset) {
      position = 'right';
    } else if (spaceLeft >= tooltipRect.width + config.offset) {
      position = 'left';
    } else {
      position = 'bottom';
    }
  }

  switch (position) {
    case 'top':
      top = targetPosition.top - tooltipRect.height - config.offset;
      left = targetPosition.left + targetPosition.width / 2 - tooltipRect.width / 2;
      break;
    case 'bottom':
      top = targetPosition.top + targetPosition.height + config.offset;
      left = targetPosition.left + targetPosition.width / 2 - tooltipRect.width / 2;
      break;
    case 'left':
      top = targetPosition.top + targetPosition.height / 2 - tooltipRect.height / 2;
      left = targetPosition.left - tooltipRect.width - config.offset;
      break;
    case 'right':
      top = targetPosition.top + targetPosition.height / 2 - tooltipRect.height / 2;
      left = targetPosition.left + targetPosition.width + config.offset;
      break;
  }

  const margin = 8;
  left = Math.max(margin, Math.min(left, viewportWidth - tooltipRect.width - margin));
  top = Math.max(margin, Math.min(top, viewportHeight - tooltipRect.height - margin));

  return { top, left, position };
}

export const useTooltipStore = create<TooltipState>((set, get) => {
  const clearTimeouts = (): void => {
    if (showTimeout) {
      clearTimeout(showTimeout);
      showTimeout = null;
    }
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
  };

  return {
    visible: false,
    content: '',
    maxWidth: DEFAULT_CONFIG.maxWidth,
    placement: null,

    showTooltip: (element, config) => {
      clearTimeouts();

      if (!isTextTruncated(element)) {
        get().hideTooltip();
        return;
      }

      const fullConfig: TooltipConfig = { ...DEFAULT_CONFIG, ...config };

      showTimeout = setTimeout(() => {
        currentTarget = element;
        currentConfig = fullConfig;
        // Show first so the host renders `.tooltip-content`, then place it.
        set({ visible: true, content: fullConfig.content, maxWidth: fullConfig.maxWidth });
        const placement = computePlacement(calculatePosition(element), fullConfig);
        set({ placement });
      }, fullConfig.delay);
    },

    hideTooltip: () => {
      clearTimeouts();
      hideTimeout = setTimeout(() => {
        set({ visible: false, placement: null });
        currentTarget = null;
      }, 150);
    },

    hideTooltipImmediate: () => {
      clearTimeouts();
      set({ visible: false, placement: null });
      currentTarget = null;
    },

    updatePosition: () => {
      if (get().visible && currentTarget) {
        const placement = computePlacement(calculatePosition(currentTarget), currentConfig);
        set({ placement });
      }
    },

    init: () => {
      if (initialized) return;
      initialized = true;

      scrollHandler = () => {
        if (scrollDebounce) clearTimeout(scrollDebounce);
        scrollDebounce = setTimeout(() => get().updatePosition(), 16); // ~60fps
      };
      resizeHandler = () => {
        if (resizeDebounce) clearTimeout(resizeDebounce);
        resizeDebounce = setTimeout(() => get().updatePosition(), 200);
      };
      window.addEventListener('scroll', scrollHandler);
      window.addEventListener('resize', resizeHandler);
    },

    destroy: () => {
      clearTimeouts();
      if (scrollHandler) window.removeEventListener('scroll', scrollHandler);
      if (resizeHandler) window.removeEventListener('resize', resizeHandler);
      if (scrollDebounce) clearTimeout(scrollDebounce);
      if (resizeDebounce) clearTimeout(resizeDebounce);
      scrollHandler = null;
      resizeHandler = null;
      scrollDebounce = null;
      resizeDebounce = null;
      currentTarget = null;
      initialized = false;
      set({ visible: false, placement: null });
    },
  };
});
